/**
 * Agency portal shell (SYR-85).
 *
 * Marketer-view shell language (sticky 248/76 sidebar, 65px top bar, slate-50
 * canvas) applied to the agency portal at /agency/*. Guards mirror the old
 * AgencyLayout: signed-in agency users only.
 */

import React, { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Drawer, FloatButton, Grid } from 'antd';
import { CustomerServiceOutlined, MobileOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { THEME } from '../../components/MarketerView/config/theme';
import { AgencyScopeProvider } from './AgencyScopeContext';
import { AgencySidebar } from './AgencySidebar';
import { AgencyTopBar } from './AgencyTopBar';

const SIDEBAR_WIDTH = 248;
const SIDEBAR_COLLAPSED = 76;

const AgencyShellInner: React.FunctionComponent = () => {
    const intl = useIntl();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [mobileBannerDismissed, setMobileBannerDismissed] = useState(
        () => localStorage.getItem('mobileBannerDismissed') === 'true',
    );

    const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

    return (
        <div
            className="agency-portal-root"
            style={{
                minHeight: '100vh',
                background: THEME.bgLayout,
                color: THEME.textBase,
                display: 'flex',
            }}
        >
            <style>{`
                .agency-portal-root button,
                .agency-portal-root input,
                .agency-portal-root select,
                .agency-portal-root textarea { font-family: inherit; }

                /* Hover = quiet border highlight, no movement (Steve found the
                   translateY "shiver" offputting). */
                .agency-portal-root .lift {
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .agency-portal-root .lift:hover {
                    border-color: #F59E0B !important;
                    box-shadow: 0 0 0 3px #FEF3C7,
                        0 4px 6px -1px rgba(0, 0, 0, 0.10),
                        0 2px 4px -1px rgba(0, 0, 0, 0.06);
                }

                @media (max-width: 767px) {
                    .agency-portal-root [style*="grid-template-columns"] {
                        grid-template-columns: minmax(0, 1fr) !important;
                    }
                    .agency-portal-content {
                        padding-left: 12px !important;
                        padding-right: 12px !important;
                    }
                }
            `}</style>

            {/* Desktop sidebar */}
            {!isMobile && (
                <div
                    style={{
                        width: sidebarWidth,
                        flexShrink: 0,
                        borderRight: `1px solid ${THEME.border}`,
                        position: 'sticky',
                        top: 0,
                        height: '100vh',
                        transition: 'width 0.2s cubic-bezier(.4,0,.2,1)',
                    }}
                >
                    <AgencySidebar collapsed={collapsed} />
                </div>
            )}

            {/* Mobile sidebar drawer */}
            {isMobile && (
                <Drawer
                    placement="left"
                    closable={false}
                    width={SIDEBAR_WIDTH}
                    open={mobileNavOpen}
                    onClose={() => setMobileNavOpen(false)}
                    styles={{ body: { padding: 0 }, header: { display: 'none' } }}
                >
                    <AgencySidebar collapsed={false} onNavigate={() => setMobileNavOpen(false)} />
                </Drawer>
            )}

            {/* Main column */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <AgencyTopBar
                    collapsed={collapsed}
                    isMobile={isMobile}
                    onToggle={() =>
                        isMobile ? setMobileNavOpen(true) : setCollapsed((c) => !c)
                    }
                />
                <main
                    className="agency-portal-content"
                    style={{
                        flex: 1,
                        width: '100%',
                        maxWidth: 1760,
                        margin: '0 auto',
                        padding: isMobile ? '20px 16px 64px' : '28px 32px 64px',
                        boxSizing: 'border-box',
                    }}
                >
                    {isMobile && !mobileBannerDismissed && (
                        <Alert
                            type="info"
                            showIcon
                            icon={<MobileOutlined />}
                            message={
                                <FormattedMessage
                                    id="onboarding.mobileBanner.title"
                                    defaultMessage="Best viewed on desktop"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="onboarding.mobileBanner.description"
                                    defaultMessage="BrandScience.ai is optimised for larger screens. For the best experience, view this on a desktop or tablet."
                                />
                            }
                            style={{ marginBottom: 16, borderRadius: 12 }}
                            closable
                            onClose={() => {
                                setMobileBannerDismissed(true);
                                localStorage.setItem('mobileBannerDismissed', 'true');
                            }}
                        />
                    )}
                    <Outlet />
                </main>
            </div>

            {!location.pathname.includes('/support') && (
                <FloatButton
                    icon={<CustomerServiceOutlined />}
                    type="primary"
                    tooltip={intl.formatMessage({
                        id: 'agency.shell.supportTooltip',
                        defaultMessage: 'Contact support',
                    })}
                    onClick={() => navigate('/agency/support')}
                />
            )}
        </div>
    );
};

export const AgencyShell: React.FunctionComponent = () => {
    const { user } = useAuth();

    if (!user) {
        return <Navigate to="/sign-in" replace />;
    }

    const roles: string[] =
        typeof user.roles === 'string' ? JSON.parse(user.roles || '[]') : user.roles || [];
    const isAgencyUser = roles.includes('AGENCY_ADMIN') || roles.includes('AGENCY_USER');
    if (!isAgencyUser) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <AgencyScopeProvider>
            <AgencyShellInner />
        </AgencyScopeProvider>
    );
};
